import isNil from 'lodash/isNil';
import React, { useState } from 'react';

import {
  Button,
  Caption,
  Grid,
  Icon,
  Stack,
  Tag,
  Text,
} from '@eluve/components';
import { formatDate } from '@eluve/date-utils';
import { useTranslation } from '@eluve/i18n';

import {
  type LabTestItem,
  safeParseCarePlanData,
  safeParseConditionData,
  safeParseFileData,
  safeParseIntakeFormData,
  safeParseLabResultData,
  safeParseLabResultsReport,
  safeParseMedicationData,
  safeParseNoteData,
  safeParseObservationData,
  safeParsePatientDetailsData,
} from './citationSchemas';

// Helper to get status color
const getStatusColor = (
  status?: string,
): 'lightgreen' | 'orange' | 'gray' | 'info' | 'yellow' => {
  if (!status) return 'gray';
  const lowerStatus = status.toLowerCase();
  if (
    lowerStatus === 'active' ||
    lowerStatus === 'confirmed' ||
    lowerStatus === 'completed'
  ) {
    return 'lightgreen';
  }
  if (lowerStatus === 'resolved' || lowerStatus === 'inactive') {
    return 'info';
  }
  if (lowerStatus === 'pending' || lowerStatus === 'draft') {
    return 'yellow';
  }
  if (lowerStatus === 'cancelled' || lowerStatus === 'stopped') {
    return 'orange';
  }
  return 'gray';
};

// Helper component for labeled values
const LabeledValue: React.FC<{
  label: string;
  value?: string | number | null;
  className?: string;
}> = ({ label, value, className }) => {
  if (isNil(value) || value === '') return null;
  return (
    <Stack gap={2} className={className} direction="horizontal">
      <Caption inline size="3xs" weight="bold" color="supporting">
        {label}:
      </Caption>
      <Text inline>{String(value)}</Text>
    </Stack>
  );
};

// Helper to format reference range from different formats
const formatReferenceRange = (
  refRange: LabTestItem['reference_range'],
): string | null => {
  if (!refRange) return null;

  if (typeof refRange === 'string') {
    return refRange;
  }

  // Handle object format with min/max
  const min = refRange.min;
  const max = refRange.max;

  if (min !== undefined && max !== undefined) {
    return `${min} - ${max}`;
  }
  if (min !== undefined) {
    return `≥ ${min}`;
  }
  if (max !== undefined) {
    return `≤ ${max}`;
  }

  return null;
};

// Single Lab Test Item Renderer (for use within LabResultRenderer)
const LabTestItemRenderer: React.FC<{ test: LabTestItem }> = ({ test }) => {
  const { t } = useTranslation();
  const value = test.result ?? test.value;
  const unit = test.units ?? test.unit ?? '';
  const refRange = formatReferenceRange(
    test.reference_range ?? test.referenceRange,
  );

  // Determine if outside normal limits
  // within_normal_limits: false means it IS outside range
  // is_outside_reference_range: true means it IS outside range
  const isOutsideRange =
    test.within_normal_limits === false ||
    test.is_outside_reference_range === true ||
    test.isOutsideReferenceRange === true;

  // Only show result details if we have a value
  const hasDetailedResult = !isNil(value);

  return (
    <Stack
      className="rounded-lg border border-border-primary bg-bg-gray-subtle p-3"
      gap={2}
    >
      <Stack justify="between" align="start" gap={2} direction="horizontal">
        <Stack gap={1} className="flex-1">
          <Stack gap={2} align="center" direction="horizontal">
            <Text inline weight="semibold">
              {test.name ?? t('appointmentTools.citations.unknownTest')}
            </Text>
            {!hasDetailedResult && test.within_normal_limits === true && (
              <Tag
                label={t('appointmentTools.citations.normal')}
                color="lightgreen"
                size="s"
              />
            )}
            {isOutsideRange && (
              <Tag
                label={t('appointmentTools.citations.abnormal')}
                color="orange"
                size="s"
              />
            )}
          </Stack>

          {hasDetailedResult && (
            <Stack
              gap={4}
              align="center"
              className="mt-1"
              direction="horizontal"
            >
              <Stack gap={1} align="center" direction="horizontal">
                <Text
                  inline
                  className={isOutsideRange ? 'text-text-warning' : ''}
                >
                  {value}
                </Text>
                {unit && (
                  <Text inline color="tertiary">
                    {unit}
                  </Text>
                )}
              </Stack>
              {refRange && (
                <Text color="tertiary" className="whitespace-nowrap">
                  {t('appointmentTools.citations.refRange', {
                    range: refRange,
                  })}
                </Text>
              )}
            </Stack>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};

// Lab Result Renderer - handles both single results and full reports
export const LabResultRenderer: React.FC<{ data: unknown }> = ({ data }) => {
  const { t } = useTranslation();
  // Try parsing as a lab results report first
  const reportResult = safeParseLabResultsReport(data);
  const tests = reportResult.success ? reportResult.data.tests : undefined;
  if (reportResult.success && tests?.length) {
    const report = reportResult.data;
    const abnormalTests = tests.filter(
      (t) =>
        t.within_normal_limits === false ||
        t.is_outside_reference_range === true ||
        t.isOutsideReferenceRange === true,
    );
    const normalTests = tests.filter(
      (t) =>
        t.within_normal_limits === true &&
        t.is_outside_reference_range !== true &&
        t.isOutsideReferenceRange !== true,
    );

    return (
      <Stack gap={3}>
        <Stack justify="between" align="center" gap={2} direction="horizontal">
          <Text inline size="md" weight="semibold">
            {t('appointmentTools.citations.labResults')}
          </Text>
          {report.date && (
            <Text inline size="xs" color="tertiary">
              {report.date}
            </Text>
          )}
        </Stack>

        {/* Summary tags */}
        <Stack gap={2} direction="horizontal">
          <Tag
            label={t('appointmentTools.citations.testsCount', {
              count: tests.length,
            })}
            color="info"
            size="s"
          />
          {abnormalTests.length > 0 && (
            <Tag
              label={t('appointmentTools.citations.abnormalCount', {
                count: abnormalTests.length,
              })}
              color="orange"
              size="s"
            />
          )}
          {normalTests.length > 0 && (
            <Tag
              label={t('appointmentTools.citations.normalCount', {
                count: normalTests.length,
              })}
              color="lightgreen"
              size="s"
            />
          )}
        </Stack>

        {/* Abnormal results first */}
        {abnormalTests.length > 0 && (
          <Stack gap={2}>
            <Caption size="3xs" color="supporting">
              {t('appointmentTools.citations.abnormalResults')}
            </Caption>
            {abnormalTests.map((test, idx) => (
              <LabTestItemRenderer key={test.name ?? idx} test={test} />
            ))}
          </Stack>
        )}

        {/* Normal results */}
        {normalTests.length > 0 && (
          <Stack gap={2}>
            <Caption size="3xs" color="supporting">
              {t('appointmentTools.citations.normalResults')}
            </Caption>
            {normalTests.map((test, idx) => (
              <LabTestItemRenderer key={test.name ?? idx} test={test} />
            ))}
          </Stack>
        )}
      </Stack>
    );
  }

  // Try legacy single lab result format
  const labResult = safeParseLabResultData(data);
  if (!labResult.success) {
    return <GenericRenderer data={data} />;
  }

  const lab = labResult.data;
  const isOutsideRange =
    lab.is_outside_reference_range ?? lab.isOutsideReferenceRange ?? false;
  const refRange = lab.reference_range ?? lab.referenceRange;
  const reportName = lab.report_name ?? lab.reportName;
  const createdAt = lab.created_at ?? lab.createdAt;

  return (
    <Stack gap={3}>
      <Stack justify="between" align="start" gap={2} direction="horizontal">
        <Stack gap={1}>
          <Text inline size="md" weight="semibold">
            {lab.name ?? t('appointmentTools.citations.unknownTest')}
          </Text>
          {reportName && (
            <Text inline size="xs" color="tertiary">
              {reportName}
            </Text>
          )}
        </Stack>
        {createdAt && (
          <Text inline size="xs" color="tertiary">
            {formatDate.toLocale(createdAt)}
          </Text>
        )}
      </Stack>

      <Stack
        className="rounded-lg border border-border-primary bg-bg-gray-subtle p-4"
        gap={2}
      >
        <Stack justify="between" align="center" gap={2} direction="horizontal">
          <Stack gap={1}>
            <Caption inline size="3xs" color="supporting">
              {t('appointmentTools.citations.result')}
            </Caption>
            <Stack gap={2} align="center" direction="horizontal">
              {isOutsideRange ? (
                <Tag
                  label={`${lab.value ?? '--'} ${lab.unit ?? ''}`}
                  color="orange"
                />
              ) : (
                <Text inline size="md">
                  {lab.value ?? '--'} {lab.unit ?? ''}
                </Text>
              )}
            </Stack>
          </Stack>

          {refRange && (
            <Stack gap={1} align="end">
              <Caption size="3xs" color="supporting">
                {t('appointmentTools.citations.referenceRange')}
              </Caption>
              <Text color="secondary">{refRange}</Text>
            </Stack>
          )}
        </Stack>

        {isOutsideRange && (
          <Stack
            gap={2}
            className="mt-3 rounded-md bg-bg-warning-subtle px-3 py-2"
            direction="horizontal"
          >
            <Icon name="AlertTriangle" className="text-text-warning" />
            <Text inline size="xs" className="text-text-warning">
              {t('appointmentTools.citations.valueOutsideRange')}
            </Text>
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};

// Medication Renderer
export const MedicationRenderer: React.FC<{ data: unknown }> = ({ data }) => {
  const { t } = useTranslation();
  const result = safeParseMedicationData(data);
  if (!result.success) {
    return <GenericRenderer data={data} />;
  }

  const med = result.data;
  const dosageInstructions =
    med.dosage_instructions ?? med.dosageInstructions ?? med.note;
  const authoredOn = med.authored_on ?? med.authoredOn;

  return (
    <Stack gap={3}>
      <Stack justify="between" align="start" gap={2} direction="horizontal">
        <Stack gap={1}>
          <Text inline size="md" weight="semibold" className="capitalize">
            {med.name ?? t('appointmentTools.citations.unknownMedication')}
          </Text>
          {med.status && (
            <Tag
              label={med.status}
              color={getStatusColor(med.status)}
              size="s"
            />
          )}
        </Stack>
        {authoredOn && (
          <Text inline size="xs" color="tertiary">
            {formatDate.toLocale(authoredOn)}
          </Text>
        )}
      </Stack>

      <Stack
        className="rounded-lg border border-border-primary bg-bg-gray-subtle p-4"
        gap={2}
      >
        <Stack gap={3}>
          {dosageInstructions && (
            <Stack gap={1}>
              <Caption size="3xs" color="supporting">
                {t('appointmentTools.citations.dosageInstructions')}
              </Caption>
              <Text>{dosageInstructions}</Text>
            </Stack>
          )}

          <Stack gap={6} direction="horizontal">
            {med.intent && (
              <Stack gap={1}>
                <Caption inline size="3xs" color="supporting">
                  {t('appointmentTools.citations.intent')}
                </Caption>
                <Text inline className="capitalize">
                  {med.intent}
                </Text>
              </Stack>
            )}
            {med.priority && (
              <Stack gap={1}>
                <Caption inline size="3xs" color="supporting">
                  {t('appointmentTools.citations.priority')}
                </Caption>
                <Text inline className="capitalize">
                  {med.priority}
                </Text>
              </Stack>
            )}
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};

// Condition Renderer
export const ConditionRenderer: React.FC<{ data: unknown }> = ({ data }) => {
  const { t } = useTranslation();
  const result = safeParseConditionData(data);
  if (!result.success) {
    return <GenericRenderer data={data} />;
  }

  const condition = result.data;
  const clinicalStatus = condition.clinical_status ?? condition.clinicalStatus;
  const verificationStatus =
    condition.verification_status ?? condition.verificationStatus;
  const recordedDate = condition.recorded_date ?? condition.recordedDate;

  return (
    <Stack gap={3}>
      <Stack justify="between" align="start" gap={2} direction="horizontal">
        <Stack gap={1}>
          <Text inline size="md" weight="semibold">
            {condition.name ??
              condition.code ??
              t('appointmentTools.citations.unknownCondition')}
          </Text>
          {condition.code && condition.name && (
            <Text inline size="xs" color="tertiary">
              {t('appointmentTools.citations.codeLabel', {
                code: condition.code,
              })}
            </Text>
          )}
        </Stack>
        {recordedDate && (
          <Text inline size="xs" color="tertiary">
            {formatDate.toLocale(recordedDate)}
          </Text>
        )}
      </Stack>

      <Stack
        className="rounded-lg border border-border-primary bg-bg-gray-subtle p-4"
        gap={2}
      >
        <Stack gap={3}>
          <Stack gap={4} direction="horizontal">
            {clinicalStatus && (
              <Stack gap={1}>
                <Caption inline size="3xs" color="supporting">
                  {t('appointmentTools.citations.clinicalStatus')}
                </Caption>
                <Tag
                  label={clinicalStatus}
                  color={getStatusColor(clinicalStatus)}
                  size="s"
                />
              </Stack>
            )}
            {verificationStatus && (
              <Stack gap={1}>
                <Caption inline size="3xs" color="supporting">
                  {t('appointmentTools.citations.verification')}
                </Caption>
                <Tag
                  label={verificationStatus}
                  color={getStatusColor(verificationStatus)}
                  size="s"
                />
              </Stack>
            )}
            {condition.category && (
              <Stack gap={1}>
                <Caption size="3xs" color="supporting">
                  {t('appointmentTools.citations.category')}
                </Caption>
                <Text>{condition.category}</Text>
              </Stack>
            )}
          </Stack>

          {condition.note && (
            <Stack gap={1}>
              <Caption size="3xs" color="supporting">
                {t('appointmentTools.citations.note')}
              </Caption>
              <Text>{condition.note}</Text>
            </Stack>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};

// Patient Details Renderer
export const PatientDetailsRenderer: React.FC<{ data: unknown }> = ({
  data,
}) => {
  const { t } = useTranslation();
  const result = safeParsePatientDetailsData(data);
  if (!result.success) {
    return <GenericRenderer data={data} />;
  }

  const patient = result.data;
  const firstName = patient.firstName ?? patient.first_name;
  const lastName = patient.lastName ?? patient.last_name;
  const dateOfBirth = patient.dateOfBirth ?? patient.date_of_birth;
  const phone =
    patient.cellPhoneNumber ?? patient.cell_phone_number ?? patient.phone;

  return (
    <Stack gap={3}>
      <Text size="md" weight="semibold">
        {[firstName, lastName].filter(Boolean).join(' ') ||
          t('appointmentTools.citations.patientFallback')}
      </Text>

      <Stack
        className="rounded-lg border border-border-primary bg-bg-gray-subtle p-4"
        gap={2}
      >
        <Grid columns={2} gap={4}>
          <LabeledValue
            label={t('appointmentTools.citations.dateOfBirth')}
            value={dateOfBirth ? formatDate.toLocale(dateOfBirth) : undefined}
          />
          <LabeledValue
            label={t('appointmentTools.citations.sex')}
            value={patient.sex ?? patient.gender}
          />
          <LabeledValue
            label={t('appointmentTools.citations.email')}
            value={patient.email}
          />
          <LabeledValue
            label={t('appointmentTools.citations.phone')}
            value={phone}
          />
          {patient.address && (
            <div className="col-span-2">
              <LabeledValue
                label={t('appointmentTools.citations.address')}
                value={patient.address}
              />
            </div>
          )}
        </Grid>
      </Stack>
    </Stack>
  );
};

// Observation Renderer
export const ObservationRenderer: React.FC<{ data: unknown }> = ({ data }) => {
  const { t } = useTranslation();
  const result = safeParseObservationData(data);
  if (!result.success) {
    return <GenericRenderer data={data} />;
  }

  const obs = result.data;
  const isOutsideRange =
    obs.is_outside_reference_range ?? obs.isOutsideReferenceRange ?? false;
  const refRange = obs.reference_range ?? obs.referenceRange;
  const timestamp = obs.source_timestamp ?? obs.sourceTimestamp;

  return (
    <Stack gap={3}>
      <Stack justify="between" align="start" gap={2} direction="horizontal">
        <Text inline size="md" weight="semibold">
          {obs.name ?? t('appointmentTools.citations.unknownObservation')}
        </Text>
        {timestamp && (
          <Text inline size="xs" color="tertiary">
            {formatDate.toLocale(timestamp)}
          </Text>
        )}
      </Stack>

      <Stack
        className="rounded-lg border border-border-primary bg-bg-gray-subtle p-4"
        gap={2}
      >
        <Stack justify="between" align="center" gap={2} direction="horizontal">
          <Stack gap={1}>
            <Caption inline size="3xs" color="supporting">
              {t('appointmentTools.citations.value')}
            </Caption>
            {isOutsideRange ? (
              <Tag
                label={`${obs.value ?? '--'} ${obs.unit ?? ''}`}
                color="orange"
              />
            ) : (
              <Text inline size="md">
                {obs.value ?? '--'} {obs.unit ?? ''}
              </Text>
            )}
          </Stack>

          {refRange && (
            <Stack gap={1} align="end">
              <Caption size="3xs" color="supporting">
                {t('appointmentTools.citations.referenceRange')}
              </Caption>
              <Text color="secondary">{refRange}</Text>
            </Stack>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};

// Care Plan Renderer
export const CarePlanRenderer: React.FC<{ data: unknown }> = ({ data }) => {
  const { t } = useTranslation();
  const result = safeParseCarePlanData(data);
  if (!result.success) {
    return <GenericRenderer data={data} />;
  }

  const plan = result.data;
  const goals = Array.isArray(plan.goals)
    ? plan.goals
    : plan.goals
      ? [plan.goals]
      : [];
  const activities = Array.isArray(plan.activity)
    ? plan.activity
    : plan.activity
      ? [plan.activity]
      : [];

  return (
    <Stack gap={3}>
      <Stack justify="between" align="start" gap={2} direction="horizontal">
        <Stack gap={1}>
          <Text inline size="md" weight="semibold">
            {plan.title ?? t('appointmentTools.citations.carePlan')}
          </Text>
          {plan.status && (
            <Tag
              label={plan.status}
              color={getStatusColor(plan.status)}
              size="s"
            />
          )}
        </Stack>
        {plan.category && (
          <Text inline size="xs" color="tertiary">
            {plan.category}
          </Text>
        )}
      </Stack>

      <Stack
        className="rounded-lg border border-border-primary bg-bg-gray-subtle p-4"
        gap={2}
      >
        <Stack gap={4}>
          {plan.description && (
            <Stack gap={1}>
              <Caption size="3xs" color="supporting">
                {t('appointmentTools.citations.description')}
              </Caption>
              <Text>{plan.description}</Text>
            </Stack>
          )}

          {goals.length > 0 && (
            <Stack gap={2}>
              <Caption size="3xs" color="supporting">
                {t('appointmentTools.citations.goals')}
              </Caption>
              <ul className="list-inside list-disc space-y-1 pl-2">
                {goals.map((goal, idx) => (
                  <li key={idx}>
                    <Text className="inline">{goal}</Text>
                  </li>
                ))}
              </ul>
            </Stack>
          )}

          {activities.length > 0 && (
            <Stack gap={2}>
              <Caption size="3xs" color="supporting">
                {t('appointmentTools.citations.activities')}
              </Caption>
              <ul className="list-inside list-disc space-y-1 pl-2">
                {activities.map((activity, idx) => (
                  <li key={idx}>
                    <Text className="inline">{activity}</Text>
                  </li>
                ))}
              </ul>
            </Stack>
          )}

          {plan.note && (
            <Stack gap={1}>
              <Caption size="3xs" color="supporting">
                {t('appointmentTools.citations.note')}
              </Caption>
              <Text>{plan.note}</Text>
            </Stack>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};

// Note Renderer (for signedNotes, unsignedNotes, tempMedicalNotes)
export const NoteRenderer: React.FC<{ data: unknown }> = ({ data }) => {
  const { t } = useTranslation();
  const result = safeParseNoteData(data);
  if (!result.success) {
    return <GenericRenderer data={data} />;
  }

  const note = result.data;
  const content = note.content ?? note.text;
  const createdAt = note.createdAt ?? note.created_at;
  const appointmentId = note.appointmentId ?? note.appointment_id;

  return (
    <Stack gap={3}>
      <Stack justify="between" align="center" gap={2} direction="horizontal">
        <Text inline size="md" weight="semibold">
          {t('appointmentTools.citations.clinicalNote')}
        </Text>
        {createdAt && (
          <Text inline size="xs" color="tertiary">
            {formatDate.toLocale(createdAt)}
          </Text>
        )}
      </Stack>

      {appointmentId && (
        <Stack gap={2} direction="horizontal">
          <Icon name="Calendar" className="text-text-tertiary" />
          <Text inline size="xs" color="tertiary">
            {t('appointmentTools.citations.appointmentLabel', {
              id: appointmentId,
            })}
          </Text>
        </Stack>
      )}

      <Stack
        className="rounded-lg border border-border-primary bg-bg-gray-subtle p-4"
        gap={2}
      >
        <Text className="whitespace-pre-wrap">
          {content ?? t('appointmentTools.citations.noContent')}
        </Text>
      </Stack>
    </Stack>
  );
};

// File Renderer (for uploadedFiles)
export const FileRenderer: React.FC<{ data: unknown }> = ({ data }) => {
  const { t } = useTranslation();
  const result = safeParseFileData(data);
  if (!result.success) {
    return <GenericRenderer data={data} />;
  }

  const file = result.data;
  const fileName = file.fileName ?? file.file_name;
  const startPage = file.startPage ?? file.start_page;
  const endPage = file.endPage ?? file.end_page;

  const pageInfo =
    startPage !== undefined
      ? endPage === undefined || startPage === endPage
        ? t('appointmentTools.citations.pageSingle', { page: startPage })
        : t('appointmentTools.citations.pageRange', {
            start: startPage,
            end: endPage,
          })
      : null;

  return (
    <Stack gap={3}>
      <Stack gap={2} align="center" direction="horizontal">
        <Icon name="FileText" size="lg" className="text-text-tertiary" />
        <Text inline size="md" weight="semibold">
          {fileName ?? t('appointmentTools.citations.uploadedFile')}
        </Text>
      </Stack>

      {pageInfo && <Tag label={pageInfo} color="info" size="s" />}

      {file.text && (
        <Stack
          className="rounded-lg border border-border-primary bg-bg-gray-subtle p-4"
          gap={2}
        >
          <Stack gap={2}>
            <Caption size="3xs" color="supporting">
              {t('appointmentTools.citations.extractedText')}
            </Caption>
            <Text className="whitespace-pre-wrap">{file.text}</Text>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};

// Smart Value Display - renders values in a human-readable format
const SmartValueDisplay: React.FC<{ value: unknown }> = ({ value }) => {
  if (isNil(value)) {
    return (
      <Text size="xs" color="tertiary">
        --
      </Text>
    );
  }

  // Primitive values
  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return <Text>{String(value)}</Text>;
  }

  // Array of strings/primitives - render as comma-separated or bullet list
  if (Array.isArray(value)) {
    // If all items are primitives and short, show comma-separated
    const allPrimitives = value.every(
      (item) => typeof item === 'string' || typeof item === 'number',
    );
    const totalLength = value.reduce(
      (acc, item) => acc + String(item).length,
      0,
    );

    if (allPrimitives && totalLength < 100 && value.length <= 3) {
      return <Text>{value.join(', ')}</Text>;
    }

    // Otherwise show as bullet list
    return (
      <Stack gap={1} className="mt-1">
        {value.map((item, idx) => (
          <Stack key={idx} gap={2} align="start" direction="horizontal">
            <Text inline size="xs" color="tertiary">
              •
            </Text>
            <SmartValueDisplay value={item} />
          </Stack>
        ))}
      </Stack>
    );
  }

  // Object - render each key-value pair
  if (typeof value === 'object') {
    const entries = Object.entries(value);
    return (
      <Stack gap={2} className="mt-1 border-l-2 border-border-secondary pl-2">
        {entries.map(([key, val]) => (
          <Stack key={key}>
            <Text size="xs" color="secondary">
              {formatFieldLabel(key)}:
            </Text>
            <Stack className="pl-2" gap={2}>
              <SmartValueDisplay value={val} />
            </Stack>
          </Stack>
        ))}
      </Stack>
    );
  }

  return <Text>{String(value)}</Text>;
};

// Intake Form Field Display - renders a single field with smart formatting
const IntakeFormField: React.FC<{ label: string; value: unknown }> = ({
  label,
  value,
}) => {
  const isComplex = typeof value === 'object' && value !== null;

  return (
    <Stack gap={1}>
      <Caption size="3xs" color="supporting">
        {label.toUpperCase()}
      </Caption>
      {isComplex ? (
        <SmartValueDisplay value={value} />
      ) : (
        <Text>{isNil(value) ? '--' : String(value)}</Text>
      )}
    </Stack>
  );
};

// Intake Form Renderer
export const IntakeFormRenderer: React.FC<{ data: unknown }> = ({ data }) => {
  const { t } = useTranslation();
  const result = safeParseIntakeFormData(data);
  if (!result.success) {
    return <GenericRenderer data={data} />;
  }

  const form = result.data;
  const patientName = form.patient_name ?? form.patientName;

  // Extract responses or other fields that aren't metadata
  const displayFields = Object.entries(form).filter(
    ([key]) =>
      !['id', 'name', 'patient_name', 'patientName', 'date'].includes(key),
  );

  return (
    <Stack gap={3}>
      <Stack justify="between" align="start" gap={2} direction="horizontal">
        <Stack gap={1}>
          <Text inline size="md" weight="semibold">
            {form.name ?? t('appointmentTools.citations.intakeForm')}
          </Text>
          {patientName && (
            <Text inline size="xs" color="tertiary">
              {t('appointmentTools.citations.patientLabel', {
                name: patientName,
              })}
            </Text>
          )}
        </Stack>
        {form.date && (
          <Text inline size="xs" color="tertiary" className="w-full">
            {formatDate.toLocale(form.date)}
          </Text>
        )}
      </Stack>

      <Stack
        className="rounded-lg border border-border-primary bg-bg-gray-subtle p-4"
        gap={2}
      >
        {form.responses ? (
          <Stack gap={4}>
            {Object.entries(form.responses).map(([key, value]) => (
              <IntakeFormField
                key={key}
                label={formatFieldLabel(key)}
                value={value}
              />
            ))}
          </Stack>
        ) : displayFields.length > 0 ? (
          <Stack gap={4}>
            {displayFields.map(([key, value]) => (
              <IntakeFormField
                key={key}
                label={formatFieldLabel(key)}
                value={value}
              />
            ))}
          </Stack>
        ) : (
          <Text color="tertiary">
            {t('appointmentTools.citations.noFormData')}
          </Text>
        )}
      </Stack>
    </Stack>
  );
};

// Helper to format field labels
const formatFieldLabel = (key: string): string => {
  return key
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
};

// Generic Object Display for nested objects
const GenericObjectDisplay: React.FC<{ data: unknown; depth?: number }> = ({
  data,
  depth = 0,
}) => {
  if (isNil(data)) {
    return (
      <Text size="xs" color="tertiary">
        --
      </Text>
    );
  }

  if (typeof data !== 'object') {
    return <Text>{String(data)}</Text>;
  }

  if (Array.isArray(data)) {
    return (
      <Stack gap={2} className={depth > 0 ? 'pl-4' : ''}>
        {data.map((item, idx) => (
          <Stack key={idx} gap={2} align="start" direction="horizontal">
            <Text inline size="xs" color="tertiary">
              •
            </Text>
            <GenericObjectDisplay data={item} depth={depth + 1} />
          </Stack>
        ))}
      </Stack>
    );
  }

  const entries = Object.entries(data);
  return (
    <Stack gap={2} className={depth > 0 ? 'pl-4' : ''}>
      {entries.map(([key, value]) => (
        <Stack key={key} gap={1}>
          <Caption size="3xs" color="supporting">
            {formatFieldLabel(key)}
          </Caption>
          <GenericObjectDisplay data={value} depth={depth + 1} />
        </Stack>
      ))}
    </Stack>
  );
};

// Generic Renderer (fallback for unknown types)
export const GenericRenderer: React.FC<{ data: unknown }> = ({ data }) => {
  const { t } = useTranslation();
  if (isNil(data)) {
    return (
      <Text color="tertiary">{t('appointmentTools.citations.noData')}</Text>
    );
  }

  if (typeof data !== 'object') {
    return (
      <Stack
        className="rounded-lg border border-border-primary bg-bg-gray-subtle p-4"
        gap={2}
      >
        <Text className="whitespace-pre-wrap">{String(data)}</Text>
      </Stack>
    );
  }

  return (
    <Stack
      className="rounded-lg border border-border-primary bg-bg-gray-subtle p-4"
      gap={2}
    >
      <GenericObjectDisplay data={data} />
    </Stack>
  );
};

// Debug JSON Renderer (collapsible)
export const DebugJsonRenderer: React.FC<{ data: unknown }> = ({ data }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <Stack gap={2}>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <Icon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size="auto" />
        {isExpanded ? 'Hide Raw JSON' : 'Show Raw JSON'}
      </Button>
      {isExpanded && (
        <Stack
          className="max-h-64 overflow-auto rounded-lg border border-border-primary bg-bg-gray-subtle p-4"
          gap={2}
        >
          <pre className="font-mono text-xs whitespace-pre-wrap text-text-secondary">
            {JSON.stringify(data, null, 2)}
          </pre>
        </Stack>
      )}
    </Stack>
  );
};

// Main renderer factory
export const getCitationRenderer = (
  sourceType: string | undefined,
  data: unknown,
): React.ReactNode => {
  if (!sourceType) {
    return <GenericRenderer data={data} />;
  }

  switch (sourceType) {
    case 'labs':
    case 'labResults':
      return <LabResultRenderer data={data} />;

    case 'medicationRequests':
      return <MedicationRenderer data={data} />;

    case 'patientConditions':
      return <ConditionRenderer data={data} />;

    case 'patientDetails':
      return <PatientDetailsRenderer data={data} />;

    case 'observations':
      return <ObservationRenderer data={data} />;

    case 'carePlans':
      return <CarePlanRenderer data={data} />;

    case 'signedNotes':
    case 'unsignedNotes':
    case 'tempMedicalNotes':
    case 'latestMedicalNote':
    case 'allMedicalNotes':
      return <NoteRenderer data={data} />;

    case 'uploadedFiles':
      return <FileRenderer data={data} />;

    case 'intakeForm':
    case 'latestIntakeConversation':
      return <IntakeFormRenderer data={data} />;

    default:
      return <GenericRenderer data={data} />;
  }
};
